import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { listPages, deletePage, duplicatePage, savePage, updateProfile, getProfile, renameFolderApi } from '../api/endpoints';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { PAGE_TEMPLATES, TEMPLATE_GROUPS } from '../config/templates';
import { FolderPlus } from 'lucide-react';
import { validateFolderName, MAX_FOLDER_NAME_LENGTH, DEFAULT_PAGE_SIZE } from '../config/folders';
import { PageRow, FolderSection, PaginationControls, SearchFilters, TemplatesGrid, MoveToFolderDialog, ConfirmDialog } from './pages-list';
import type { Page, FolderSettings } from '../types';

type TabScope = 'mine' | 'team' | 'organization' | 'templates';
type VisibilityFilter = '' | 'private' | 'team' | 'organization';
type StatusFilter = '' | 'draft' | 'published';
type SortOrder = 'newest' | 'oldest';

export function PagesListPage() {
  useDocumentTitle('Meine Seiten');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [tab, setTab] = useState<TabScope>('mine');

  // Filter & search state
  const [search, setSearch] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading } = useQuery({
    queryKey: ['pages', tab],
    queryFn: () => listPages(tab === 'team' ? { scope: 'team' } : tab === 'organization' ? { scope: 'organization' } : undefined),
    enabled: tab !== 'templates',
  });

  const deleteM = useMutation({
    mutationFn: deletePage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      setConfirmDelete(null);
    },
  });

  const duplicateM = useMutation({
    mutationFn: duplicatePage,
    onSuccess: (newPage) => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      navigate(`/editor/${newPage.id}`);
    },
  });

  const toggleStatus = useMutation({
    mutationFn: (p: Page) =>
      savePage({
        id: p.id,
        templateId: p.templateId,
        title: p.title,
        slotCount: p.slotCount,
        status: p.status === 'draft' ? 'published' : 'draft',
        mainAssignments: p.mainAssignments,
        footerAssignments: p.footerAssignments,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pages'] }),
  });

  const isTeamTab = tab === 'team' || tab === 'organization';
  const isTemplatesTab = tab === 'templates';
  const isMineTab = tab === 'mine';

  // Profile for folder settings
  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    staleTime: 5 * 60 * 1000,
  });
  const folderSettings: Record<string, FolderSettings> = (profileQuery.data?.folderSettings as Record<string, FolderSettings>) ?? {};

  // Folder state
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingFolder, setRenamingFolder] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [folderError, setFolderError] = useState<string | null>(null);
  const [confirmDeleteFolder, setConfirmDeleteFolder] = useState<string | null>(null);
  const [movingPage, setMovingPage] = useState<string | null>(null);
  const [editingFolderSettings, setEditingFolderSettings] = useState<string | null>(null);

  // Stable dependency key for folder names
  const folderNamesKey = useMemo(
    () => Object.keys(folderSettings).sort().join(','),
    [folderSettings]
  );

  // Collapse all folders by default (only add new ones, don't override user's toggles)
  useEffect(() => {
    const folderNames = Object.keys(folderSettings);
    setCollapsedFolders((prev) => {
      const updated = new Set(prev);
      let changed = false;
      for (const name of folderNames) {
        if (!updated.has(name)) {
          updated.add(name);
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, [folderNamesKey]);

  const toggleFolder = (name: string) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // Folder mutations
  const updateFolderSettings = useMutation({
    mutationFn: (newSettings: Record<string, FolderSettings>) =>
      updateProfile({ folderSettings: newSettings }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  });

  const movePageToFolder = useMutation({
    mutationFn: ({ pageData, folder }: { pageData: Page; folder: string | undefined }) =>
      savePage({
        id: pageData.id,
        templateId: pageData.templateId,
        title: pageData.title,
        slotCount: pageData.slotCount,
        status: pageData.status,
        mainAssignments: pageData.mainAssignments,
        footerAssignments: pageData.footerAssignments,
        folder: folder ?? '',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      setMovingPage(null);
    },
  });

  const renameFolder = useMutation({
    mutationFn: ({ oldName, newName }: { oldName: string; newName: string }) =>
      renameFolderApi(oldName, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setRenamingFolder(null);
    },
  });

  const deleteFolder = useMutation({
    mutationFn: async (folderName: string) => {
      const items = data?.items ?? [];
      const pagesInFolder = items.filter((p) => p.folder === folderName);
      await Promise.all(
        pagesInFolder.map((p) =>
          savePage({
            id: p.id,
            templateId: p.templateId,
            title: p.title,
            slotCount: p.slotCount,
            status: p.status,
            mainAssignments: p.mainAssignments,
            footerAssignments: p.footerAssignments,
            folder: '',
          })
        )
      );
      const current = { ...folderSettings };
      delete current[folderName];
      await updateProfile({ folderSettings: current });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setConfirmDeleteFolder(null);
    },
  });



  // Reorder pages within a folder (drag & drop)
  const handleReorderPages = async (_folderName: string, orderedIds: string[]) => {
    const items = data?.items ?? [];
    const promises = orderedIds.map((id, idx) => {
      const page = items.find((p) => p.id === id);
      if (!page || page.position === idx) return null;
      return savePage({
        id: page.id,
        templateId: page.templateId,
        title: page.title,
        slotCount: page.slotCount,
        status: page.status,
        mainAssignments: page.mainAssignments,
        footerAssignments: page.footerAssignments,
        position: idx,
      });
    });
    await Promise.all(promises.filter(Boolean));
    queryClient.invalidateQueries({ queryKey: ['pages'] });
  };

  // Client-side filter, search, sort
  const filteredItems = useMemo(() => {
    let items = data?.items ?? [];
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((p) =>
        p.title.toLowerCase().includes(q) ||
        (p.createdByName && p.createdByName.toLowerCase().includes(q))
      );
    }
    if (!isTeamTab && visibilityFilter) {
      items = items.filter((p) => p.visibility === visibilityFilter);
    }
    if (!isTeamTab && statusFilter) {
      items = items.filter((p) => p.status === statusFilter);
    }
    const sorted = [...items];
    sorted.sort((a, b) => {
      const dateA = new Date(a.updatedAt).getTime();
      const dateB = new Date(b.updatedAt).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    return sorted;
  }, [data, search, visibilityFilter, statusFilter, sortOrder, isTeamTab]);

  // Folder grouping for "mine" tab
  const folderGroups = useMemo(() => {
    if (!isMineTab) return null;
    const folders = new Map<string, Page[]>();
    const rootPages: Page[] = [];
    for (const p of filteredItems) {
      if (p.folder) {
        if (!folders.has(p.folder)) folders.set(p.folder, []);
        folders.get(p.folder)!.push(p);
      } else {
        rootPages.push(p);
      }
    }
    for (const name of Object.keys(folderSettings)) {
      if (!folders.has(name)) folders.set(name, []);
    }
    // Sort pages within each folder by position
    for (const [, pages] of folders) {
      pages.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }
    const sortedFolders = [...folders.entries()].sort((a, b) => a[0].localeCompare(b[0], 'de'));
    return { folders: sortedFolders, rootPages };
  }, [filteredItems, isMineTab, folderSettings]);

  const mineTopLevelCount = folderGroups
    ? folderGroups.folders.length + folderGroups.rootPages.length
    : 0;

  const totalPages = isMineTab
    ? Math.max(1, Math.ceil(mineTopLevelCount / pageSize))
    : Math.max(1, Math.ceil(filteredItems.length / pageSize));

  const visibleItems = filteredItems.slice(page * pageSize, (page + 1) * pageSize);

  const paginatedFolderView = useMemo(() => {
    if (!folderGroups) return null;
    type TopItem = { kind: 'folder'; name: string; pages: Page[] } | { kind: 'page'; page: Page };
    const topLevel: TopItem[] = [
      ...folderGroups.folders.map(([name, pages]) => ({ kind: 'folder' as const, name, pages })),
      ...folderGroups.rootPages.map((p) => ({ kind: 'page' as const, page: p })),
    ];
    const start = page * pageSize;
    const slice = topLevel.slice(start, start + pageSize);
    const folders: [string, Page[]][] = [];
    const rootPages: Page[] = [];
    for (const item of slice) {
      if (item.kind === 'folder') folders.push([item.name, item.pages]);
      else rootPages.push(item.page);
    }
    return { folders, rootPages };
  }, [folderGroups, page, pageSize]);

  const resetPage = () => setPage(0);

  // Folder action handlers
  const handleRenameSubmit = (oldName: string, newName: string) => {
    const error = validateFolderName(newName, Object.keys(folderSettings), oldName);
    if (error) { setFolderError(error); return; }
    renameFolder.mutate({ oldName, newName });
    setFolderError(null);
  };

  const handleCreateFolder = (e: React.FormEvent) => {
    e.preventDefault();
    const name = newFolderName.trim();
    const error = validateFolderName(name, Object.keys(folderSettings));
    if (error) { setFolderError(error); return; }
    const current = { ...folderSettings };
    if (!current[name]) {
      current[name] = { icon: '📁', color: 'gray' };
      updateFolderSettings.mutate(current);
    }
    setNewFolderName('');
    setFolderError(null);
    setCreatingFolder(false);
  };

  const handleUpdateIcon = (folderName: string, icon: string) => {
    const current = { ...folderSettings };
    current[folderName] = { ...(current[folderName] ?? { icon: '📁', color: 'gray' }), icon };
    updateFolderSettings.mutate(current);
  };

  const handleUpdateColor = (folderName: string, color: string) => {
    const current = { ...folderSettings };
    current[folderName] = { ...(current[folderName] ?? { icon: '📁', color: 'gray' }), color };
    updateFolderSettings.mutate(current);
  };

  // Find page data for move dialog
  const movingPageData = movingPage ? filteredItems.find((p) => p.id === movingPage) : null;
  const allFolderNames = folderGroups ? folderGroups.folders.map(([n]) => n) : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{tab === 'templates' ? 'Seiten-Vorlagen' : tab === 'team' ? 'Team-Seiten' : tab === 'organization' ? 'Einrichtung-Seiten' : 'Meine Seiten'}</h1>
          <p className="text-gray-500 mt-1">
            {isTemplatesTab
              ? `${PAGE_TEMPLATES.length} Vorlagen in ${TEMPLATE_GROUPS.length} Kategorien`
              : <>
                  {filteredItems.length} Seiten
                  {!isMineTab && totalPages > 1 && ` — Seite ${page + 1} von ${totalPages}`}
                </>
            }
          </p>
        </div>
        <div className="flex gap-2">
          {!isTemplatesTab && (
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['pages'] })}
            className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            aria-label="Aktualisieren"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
          </button>
          )}
          {!isTeamTab && !isTemplatesTab && (
            <Link
              to="/editor"
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
              Neue Seite
            </Link>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {(['mine', 'team', 'organization', 'templates'] as const).map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); resetPage(); }}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'mine' ? 'Meine Seiten' : t === 'team' ? 'Team-Seiten' : t === 'organization' ? 'Einrichtung-Seiten' : 'Seiten-Vorlagen'}
          </button>
        ))}
      </div>

      {/* Templates Grid */}
      {isTemplatesTab ? (
        <TemplatesGrid />
      ) : (
      <>
      {/* Search & Filters */}
      <SearchFilters
        search={search}
        visibilityFilter={visibilityFilter}
        statusFilter={statusFilter}
        sortOrder={sortOrder}
        isTeamTab={isTeamTab}
        onSearchChange={(v) => { setSearch(v); resetPage(); }}
        onVisibilityChange={(v) => { setVisibilityFilter(v); resetPage(); }}
        onStatusChange={(v) => { setStatusFilter(v); resetPage(); }}
        onSortChange={(v) => { setSortOrder(v); resetPage(); }}
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : isMineTab && paginatedFolderView ? (
        /* ── Mine tab with folder grouping ── */
        <>
          {/* Folder create button */}
          <div className="flex items-center gap-2">
            {creatingFolder ? (
              <form className="flex items-center gap-2" onSubmit={handleCreateFolder}>
                <input
                  type="text"
                  autoFocus
                  maxLength={MAX_FOLDER_NAME_LENGTH}
                  value={newFolderName}
                  onChange={(e) => { setNewFolderName(e.target.value); setFolderError(null); }}
                  placeholder="Ordnername…"
                  className={`border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${folderError ? 'border-red-400' : 'border-gray-300'}`}
                  onKeyDown={(e) => { if (e.key === 'Escape') { setCreatingFolder(false); setFolderError(null); } }}
                />
                {folderError && <span className="text-xs text-red-500">{folderError}</span>}
                <button type="submit" className="px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700">Erstellen</button>
                <button type="button" onClick={() => { setCreatingFolder(false); setFolderError(null); }} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700">Abbrechen</button>
              </form>
            ) : (
              <button
                onClick={() => setCreatingFolder(true)}
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-indigo-600 transition-colors"
              >
                <FolderPlus className="w-4 h-4" />
                Ordner erstellen
              </button>
            )}
          </div>

          {filteredItems.length === 0 && folderGroups!.folders.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
              {search || visibilityFilter || statusFilter ? (
                <p className="text-gray-500 text-lg">Keine Seiten für diese Filter gefunden.</p>
              ) : (
                <>
                  <p className="text-gray-500 text-lg">Noch keine Seiten vorhanden.</p>
                  <Link to="/editor" className="text-indigo-600 font-medium hover:text-indigo-800 mt-2 inline-block">Erste Seite erstellen →</Link>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Folder groups (paginated) */}
              {paginatedFolderView.folders.map(([folderName, pages]) => (
                <FolderSection
                  key={folderName}
                  folderName={folderName}
                  pages={pages}
                  settings={folderSettings[folderName] ?? { icon: '📁', color: 'gray' }}
                  isCollapsed={collapsedFolders.has(folderName)}
                  isTeamTab={isTeamTab}
                  isMineTab={isMineTab}
                  renamingFolder={renamingFolder}
                  renameValue={renameValue}
                  folderError={folderError}
                  editingFolderSettings={editingFolderSettings}
                  duplicateIsPending={duplicateM.isPending}
                  onToggleCollapse={toggleFolder}
                  onStartRename={(name, value) => { setRenamingFolder(name); setRenameValue(value); }}
                  onRenameChange={(v) => { setRenameValue(v); setFolderError(null); }}
                  onRenameSubmit={handleRenameSubmit}
                  onCancelRename={() => { setRenamingFolder(null); setFolderError(null); }}
                  onDeleteFolder={(name) => setConfirmDeleteFolder(name)}
                  onToggleSettings={setEditingFolderSettings}
                  onUpdateIcon={handleUpdateIcon}
                  onUpdateColor={handleUpdateColor}
                  onMoveToFolder={setMovingPage}
                  onToggleStatus={(p) => toggleStatus.mutate(p)}
                  onDuplicate={(id) => duplicateM.mutate(id)}
                  onDelete={setConfirmDelete}
                  onReorderPages={handleReorderPages}
                />
              ))}

              {/* Root pages (without folder) */}
              {paginatedFolderView.rootPages.length > 0 && (
                <>
                  {paginatedFolderView.folders.length > 0 && (
                    <div className="flex items-center gap-2 pt-2">
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Ohne Ordner</span>
                      <div className="flex-1 border-t border-gray-200" />
                    </div>
                  )}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100 overflow-hidden">
                    {paginatedFolderView.rootPages.map((p) => (
                      <PageRow
                        key={p.id}
                        page={p}
                        isMineTab={isMineTab}
                        isTeamTab={isTeamTab}
                        onMoveToFolder={setMovingPage}
                        onToggleStatus={(pg) => toggleStatus.mutate(pg)}
                        onDuplicate={(id) => duplicateM.mutate(id)}
                        onDelete={setConfirmDelete}
                        duplicateIsPending={duplicateM.isPending}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <PaginationControls
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); resetPage(); }}
          />
        </>
      ) : visibleItems.length ? (
        /* ── Team/Org tab: flat list with pagination ── */
        <>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100 overflow-hidden">
          {visibleItems.map((p) => (
            <PageRow
              key={p.id}
              page={p}
              isMineTab={isMineTab}
              isTeamTab={isTeamTab}
              onMoveToFolder={setMovingPage}
              onToggleStatus={(pg) => toggleStatus.mutate(pg)}
              onDuplicate={(id) => duplicateM.mutate(id)}
              onDelete={setConfirmDelete}
              duplicateIsPending={duplicateM.isPending}
            />
          ))}
        </div>
        <PaginationControls
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); resetPage(); }}
        />
        </>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
          {isTeamTab ? (
            <>
              <p className="text-gray-500 text-lg">{tab === 'organization' ? 'Keine Einrichtung-Seiten gefunden.' : 'Keine Team-Seiten gefunden.'}</p>
              <p className="text-gray-400 text-sm mt-2">{tab === 'organization' ? 'Geteilte Seiten aus deiner Einrichtung erscheinen hier.' : 'Geteilte Seiten von Teammitgliedern erscheinen hier.'}</p>
            </>
          ) : search || visibilityFilter || statusFilter ? (
            <p className="text-gray-500 text-lg">Keine Seiten für diese Filter gefunden.</p>
          ) : (
            <>
              <p className="text-gray-500 text-lg">Noch keine Seiten vorhanden.</p>
              <Link to="/editor" className="text-indigo-600 font-medium hover:text-indigo-800 mt-2 inline-block">Erste Seite erstellen →</Link>
            </>
          )}
        </div>
      )}
      </>
      )}

      {/* Delete page confirmation */}
      {confirmDelete && (
        <ConfirmDialog
          title="Seite löschen?"
          description="Diese Aktion kann nicht rückgängig gemacht werden."
          confirmLabel="Löschen"
          pendingLabel="Löschen…"
          isPending={deleteM.isPending}
          onConfirm={() => deleteM.mutate(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Delete folder confirmation */}
      {confirmDeleteFolder && (
        <ConfirmDialog
          title={`Ordner „${confirmDeleteFolder}" löschen?`}
          description="Die Seiten im Ordner werden nicht gelöscht, sondern in den Hauptbereich verschoben."
          confirmLabel="Ordner löschen"
          pendingLabel="Löschen…"
          isPending={deleteFolder.isPending}
          onConfirm={() => deleteFolder.mutate(confirmDeleteFolder)}
          onCancel={() => setConfirmDeleteFolder(null)}
        />
      )}

      {/* Move page to folder */}
      {movingPageData && (
        <MoveToFolderDialog
          pageData={movingPageData}
          folderNames={allFolderNames}
          folderSettings={folderSettings}
          onMove={(params) => movePageToFolder.mutate(params)}
          onClose={() => setMovingPage(null)}
        />
      )}
    </div>
  );
}
